import type { Table } from '@tanstack/react-table'

import type { Transaction } from '@/modules/projects'
import { humanizeText } from '@/utils'

const pluralizeTransactions = (count: number) =>
  `${count} ${count === 1 ? 'transaction' : 'transactions'}`

const isPresent = (value: string | null | undefined): value is string =>
  !!value?.trim() && value !== 'Null'

export const buildAccountNumberOptions = (table: Table<Transaction>) => {
  const byAccount = new Map<
    string,
    { institution: string; accountType: string; count: number }
  >()
  table.getCoreRowModel().rows.forEach((row) => {
    const { accountNumber, institution, accountType } = row.original
    if (!isPresent(accountNumber)) return
    const existing = byAccount.get(accountNumber)
    if (existing) {
      existing.count += 1
    } else {
      byAccount.set(accountNumber, {
        institution: institution || '—',
        accountType: accountType || '—',
        count: 1,
      })
    }
  })
  return Array.from(byAccount.entries())
    .sort(([first], [second]) => first.localeCompare(second))
    .map(([accountNumber, { institution, accountType, count }]) => ({
      value: accountNumber,
      label: accountNumber,
      description: `${institution} · ${accountType} · ${pluralizeTransactions(count)}`,
    }))
}

export type CountedField =
  | 'statementFileName'
  | 'category'
  | 'categoryDetail'
  | 'accountOwner'
  | 'accountType'
  | 'institution'
  | 'kind'
  | 'paymentChannel'
  | 'shareNumber'

const COUNTED_FIELDS = [
  'statementFileName',
  'category',
  'categoryDetail',
  'accountOwner',
  'accountType',
  'institution',
  'kind',
  'paymentChannel',
  'shareNumber',
] as const satisfies readonly CountedField[]

export const isCountedField = (field: string): field is CountedField =>
  (COUNTED_FIELDS as readonly string[]).includes(field)

export const COUNTED_FIELD_LABEL_FORMATTERS: Partial<
  Record<CountedField, (value: string) => string>
> = {
  kind: humanizeText,
}

export const buildCountedOptions = (
  table: Table<Transaction>,
  field: CountedField,
  formatLabel: (value: string) => string = (value) => value,
) => {
  const byValue = new Map<string, number>()
  table.getCoreRowModel().rows.forEach((row) => {
    const value = row.original[field]
    if (!isPresent(value)) return
    byValue.set(value, (byValue.get(value) ?? 0) + 1)
  })
  return Array.from(byValue.entries())
    .sort(([first], [second]) => first.localeCompare(second))
    .map(([value, count]) => ({
      value,
      label: formatLabel(value),
      description: pluralizeTransactions(count),
    }))
}
